import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import GameCard from '@/components/base/GameCard';
import { categories } from '@/mocks/categories';
import { getGamesByCategory, type Game } from '@/mocks/games';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'action-adventure');
  const [filteredGames, setFilteredGames] = useState<Game[]>([]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    setFilteredGames(getGamesByCategory(activeCategory));
  }, [activeCategory]);

  useEffect(() => {
    if (currentCategory) {
      document.title = `${currentCategory.name}游戏 - 黄油游戏库`;
    }
  }, [currentCategory]);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="px-4 md:px-6 bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '游戏分类', href: '/category/action-adventure' },
                { label: currentCategory?.name || '' },
              ]}
            />
          </div>
        </div>

        <section className="py-8 md:py-12 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 text-center mb-2">
              游戏分类
            </h1>
            <p className="text-sm text-foreground-600 text-center mb-8">
              选择你感兴趣的游戏类型，开始探索独立游戏世界
            </p>

            <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {currentCategory && (
              <div className="mb-8 p-5 bg-background-100 rounded-lg border border-background-200/70">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                    <i className={`${currentCategory.icon} text-lg text-primary-600`}></i>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground-900">{currentCategory.name}</h2>
                    <p className="text-sm text-foreground-600">{currentCategory.description}</p>
                  </div>
                </div>
              </div>
            )}

            {filteredGames.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-product-shop>
                {filteredGames.map((game) => (
                  <GameCard key={game.id} game={game} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-gamepad-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无游戏</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}